import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import BaseTabs from 'components/atoms/base-tabs';
import BaseLoader from 'components/atoms/base-loader';
import BaseButton from 'components/atoms/baseButton';
import PlusIcon from 'components/atoms/icons/plus.svg?react';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import BaseCheckbox from 'components/atoms/baseCheckbox';
import { createPortal } from 'react-dom';
import { ASSIGN_TOOL_ID } from 'infrastructure/consts';

import ExpandButton from './expand-button';
import s from './styles.module.scss';
import KeyNotesContent from './key-notes-content';
import { useKeyNote } from './hooks/use-key-note';
import { CreateOrUpdateKeyNoteModal } from './modal/create-or-update-key-note';
import Trigger from './trigger';

interface IAdminPatientKeyNotesProps {
  patientGuid: string;
  canChangeModule?: boolean;
  opened?: boolean;
}

const AdminPatientKeyNotes: React.FC<IAdminPatientKeyNotesProps> = ({
  patientGuid,
  canChangeModule = true,
  opened = false,
}) => {
  const {
    currentModule,
    expanded,
    isDataEmpty,
    loading,
    isEditMode,
    tabs,
    data,
    showExpired,
    isLoading,
    open,
    handleTabClick,
    handleSave,
    setIsEditMode,
    setExpanded,
    onOpen,
    setShowExpired,
  } = useKeyNote({
    patientGuid,
    canChangeModule,
    opened,
  });
  const { isReadOnlyRole } = useCheckRoleAccess();
  const { t } = useTranslation();
  const onCancel = () => setIsEditMode(false);
  const [hasAssignToolNearby, setHasAssignToolNearby] = useState(false);

  const ROOT_ELEMENT = document.getElementById('root');
  useEffect(() => {
    const hasAssignTool = !!ROOT_ELEMENT?.querySelector(`#${ASSIGN_TOOL_ID}`);
    setHasAssignToolNearby(hasAssignTool);
  }, []);

  return (
    <>
      <CreateOrUpdateKeyNoteModal
        onCancel={onCancel}
        moduleType={currentModule}
        isOpen={isEditMode}
        isLoading={isLoading}
        onSave={(values) => {
          handleSave(values);
        }}
      />
      {createPortal(
        <>
          <Trigger
            setExpanded={setExpanded}
            onOpen={onOpen}
            opened={opened}
            isStandalone
            hasAssignToolNearby={hasAssignToolNearby}
          />
          <div
            id="admin-patient-key-note"
            data-edit-mode={isEditMode}
            data-open={open}
            data-cy="admin-patient-key-note-wrapper"
            className={classNames(s['admin-patient-key-note-wrapper'], {
              [s.expanded]: expanded,
            })}
          >
            <div className={s['key-note-wrapper']}>
              <Trigger
                setExpanded={setExpanded}
                onOpen={onOpen}
                opened={opened}
              />
              <div className={s['key-note-body']}>
                <div className={s['body-header']}>
                  <BaseTabs
                    tabs={tabs}
                    activeKey={currentModule}
                    onChange={(tab) => handleTabClick(tab.key)}
                    size="small"
                    type="rounded"
                  />
                  <div className={classNames(s.flex, s['gap-10'])}>
                    <BaseButton
                      type="secondary"
                      label={t('labels.showExpiredEntries')}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setShowExpired(!showExpired);
                      }}
                      disabled={isReadOnlyRole || loading}
                      className={s['show-expired']}
                      icon={
                        <BaseCheckbox
                          checked={showExpired}
                          onChange={setShowExpired}
                        />
                      }
                    />
                    <BaseButton
                      type="primary"
                      label={t('controls.add')}
                      onClick={() => {
                        setIsEditMode(true);
                      }}
                      disabled={isReadOnlyRole || loading}
                      icon={<PlusIcon />}
                      className={s['add-btn']}
                    />
                  </div>
                </div>
                {loading ? (
                  <BaseLoader loading className={s.loader} />
                ) : (
                  <div data-open={expanded} className={s['body-content']}>
                    <KeyNotesContent
                      patientGuid={patientGuid}
                      module={currentModule}
                      isDataEmpty={isDataEmpty}
                      data={data}
                      showExpired={showExpired}
                    />
                  </div>
                )}
                <div
                  className={classNames(s['body-actions'], {
                    [s['edit-mode']]: isEditMode,
                  })}
                >
                  <ExpandButton expanded={expanded} setExpanded={setExpanded} />
                </div>
              </div>
            </div>
          </div>
        </>,
        ROOT_ELEMENT!,
      )}
    </>
  );
};

export default AdminPatientKeyNotes;
